import React from 'react';
import Image from 'next/image';
import posthog from 'posthog-js';
import styled from 'styled-components';
import StyledButton from '@makeship/core/lib/components/Button';
import { P1, P1Styles } from '../Typography';
import Container from '../Container';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';

const MastheadWrapper = styled.div<{ backgroundImage?: string }>`
  height: 670px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    height: 700px;
  }
`;

const MastheadContainer = styled(Container)`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  margin: 0 16px;
  margin-bottom: 5px;
  justify-content: space-between;
  width: 65%;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const DesktopPlushieImageWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: block;
    width: 500px;
    height: 500px;
  }
`;

const MobilePlushieImageWrapper = styled.div`
  width: 282px;
  height: 282px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const DescriptionWrapper = styled.div<{ backgroundColor: string }>`
  background: ${({ backgroundColor }) => backgroundColor};
  display: flex;
  padding: 24px 16px;
  border-radius: 10px;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: 328px;
  max-height: 270px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    justify-content: space-between;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    max-width: 468px;
    max-height: 282px;
  }
`;

const LogoAndDescriptionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  justify-content: center;
`;

const DescriptionText = styled(P1)<{ color: string }>`
  color: ${({ color }) => color};
  font-weight: 700;
  text-align: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    ${P1Styles}
    font-weight: 700;
    color: ${({ color }) => color};
  }
`;

const InfoStyledButton = styled(StyledButton.Tertiary)<{ backgroundColor: string; textColor: string }>`
  background: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  text-wrap: nowrap;
  border: transparent;
  padding: 14px 24px;
  width: 280px;
  &:active,
  &:hover {
    background: ${({ backgroundColor }) => backgroundColor};
    border: transparent;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InfoContentWrapper = styled.div``;

const ImageDesktopWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: block;
  }
`;

const ImageMobileWrapper = styled.div`
  display: block;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: none;
  }
`;

type MastheadCarouselItemProps = {
  descriptionText: string;
  descriptionTextColor: string;
  descriptionBackgroundColor: string;
  ctaText: string;
  ctaTextColor: string;
  ctaBackgroundColor: string;
  ctaLink: string;
  productImage: string;
  logoImage: string;
  backgroundImage: string;
  collectionName: string;
};

const MastheadCarouselItem: React.FC<MastheadCarouselItemProps> = ({
  descriptionText,
  descriptionTextColor,
  descriptionBackgroundColor,
  ctaText,
  ctaTextColor,
  ctaBackgroundColor,
  ctaLink,
  productImage,
  logoImage,
  backgroundImage,
  collectionName,
}: MastheadCarouselItemProps) => {
  const { state } = useStore();

  return (
    <MastheadWrapper backgroundImage={backgroundImage}>
      <MastheadContainer>
        <DescriptionWrapper backgroundColor={descriptionBackgroundColor}>
          <LogoAndDescriptionWrapper>
            <ImageDesktopWrapper>
              <Image
                src={logoImage}
                width={320}
                height={75}
                quality={90}
                alt={`${collectionName} logo`}
                priority
                objectFit="contain"
              />
            </ImageDesktopWrapper>
            <ImageMobileWrapper>
              <Image
                src={logoImage}
                width={218}
                height={51}
                quality={90}
                alt={`${collectionName} logo`}
                priority
                objectFit="contain"
              />
            </ImageMobileWrapper>
          </LogoAndDescriptionWrapper>
          <InfoContentWrapper>
            <DescriptionText color={descriptionTextColor}>{descriptionText}</DescriptionText>
          </InfoContentWrapper>
          <ButtonContainer>
            <a href={`${ctaLink}`}>
              <InfoStyledButton
                backgroundColor={ctaBackgroundColor}
                textColor={ctaTextColor}
                onClick={() =>
                  posthog.capture(`homepage_masthead_cta_click`, {
                    creator: collectionName,
                    isUserLoggedIn: isUserLoggedIn(state.user),
                  })
                }
              >
                {ctaText}
              </InfoStyledButton>
            </a>
          </ButtonContainer>
        </DescriptionWrapper>
        <div>
          <a
            href={ctaLink}
            onClick={() =>
              posthog.capture(`homepage_masthead_image_click`, {
                creator: collectionName,
                isUserLoggedIn: isUserLoggedIn(state.user),
              })
            }
          >
            <DesktopPlushieImageWrapper>
              <Image src={productImage} width="500" height="500" alt={`${collectionName} products`} priority />
            </DesktopPlushieImageWrapper>
            <MobilePlushieImageWrapper>
              <Image src={productImage} width="282" height="282" alt={`${collectionName} products`} priority />
            </MobilePlushieImageWrapper>
          </a>
        </div>
      </MastheadContainer>
    </MastheadWrapper>
  );
};
export default MastheadCarouselItem;
